import { FC, useEffect, useState } from 'react';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import type { AdminUser, AgentAccessAuditEntry } from '@/types/admin';

interface Props {
  userId: string;
}

/**
 * Seção "Painéis executivos" no AdminUserFormPage.
 *
 * Gêmea da seção do Balanceamento de Linha, sobre a flag dos painéis (`/paineis`).
 * Os painéis não entram na Sidebar, então liberar alguém só é possível por aqui.
 */
const DashboardFeatureToggleSection: FC<Props> = ({ userId }) => {
  const { showToast } = useToast();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [reason, setReason] = useState('');
  const [audit, setAudit] = useState<AgentAccessAuditEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [u, a] = await Promise.all([
          adminService.getUser(userId),
          adminService.getUserDashboardAccessAudit(userId),
        ]);
        if (cancelled) return;
        setUser(u);
        setEnabled(u.dashboard_feature_enabled);
        setAudit(a);
      } catch {
        if (!cancelled) showToast('Erro ao carregar acesso aos painéis', 'error');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, showToast]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await adminService.updateUser(userId, {
        dashboard_feature_enabled: enabled,
        dashboard_feature_reason: reason || undefined,
      });
      setUser(updated);
      setReason('');
      const fresh = await adminService.getUserDashboardAccessAudit(userId);
      setAudit(fresh);
      showToast('Acesso atualizado', 'success');
    } catch {
      showToast('Erro ao atualizar acesso', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) return null;

  const sectorLiberates = !!user.sector_dashboard_feature_enabled;
  const dirty = enabled !== user.dashboard_feature_enabled;

  return (
    <div
      data-testid="dashboard-feature-section"
      className="mt-8 bg-surface-card border border-theme-border rounded-xl p-5 space-y-4"
    >
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <span className="material-symbols-outlined text-accent">monitoring</span>
            Painéis executivos
          </h2>
          <p className="text-sm text-content-muted mt-1">
            Controle se este usuário pode abrir os painéis executivos (ex.: S&OP da
            Equiplex, com números de produção e faturamento). Só ADMIN tem acesso sem esta
            flag — CURATOR também precisa ser liberado. Os painéis não aparecem na Sidebar;
            o acesso é por link direto.
          </p>
        </div>
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          data-testid="dashboard-feature-toggle"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-5 w-5"
        />
        <span className="text-sm font-medium">Liberar os painéis pra este usuário</span>
      </label>

      {sectorLiberates && (
        <div
          data-testid="sector-liberates-hint"
          className="text-xs bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-lg px-3 py-2"
        >
          O setor <strong>{user.sector?.name}</strong> já libera os painéis pra todos os
          membros — este toggle só importa se o setor for desativado depois.
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-content-muted mb-1.5">
          Motivo (opcional, vai pro audit)
        </label>
        <textarea
          data-testid="dashboard-feature-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex.: pedido da diretoria industrial"
          rows={2}
          className="w-full bg-surface-primary border border-theme-border rounded-lg px-3 py-2 text-sm"
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={!dirty || isSaving}
          data-testid="dashboard-feature-save"
          className="bg-accent hover:bg-accent-hover text-accent-contrast px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {isSaving ? 'Salvando...' : 'Salvar'}
        </button>
        <button
          onClick={() => setShowHistory((s) => !s)}
          data-testid="dashboard-feature-history-toggle"
          className="text-sm text-content-muted hover:text-content-primary underline"
        >
          {showHistory ? 'Ocultar' : 'Mostrar'} histórico ({audit.length})
        </button>
      </div>

      {showHistory && (
        <div data-testid="dashboard-feature-history" className="border-t border-theme-border pt-3 space-y-2">
          {audit.length === 0 ? (
            <p className="text-xs text-content-muted">Sem mudanças registradas.</p>
          ) : (
            audit.map((log) => (
              <div key={log.id} className="text-xs flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 rounded ${
                    log.new_value
                      ? 'bg-emerald-500/20 text-emerald-300'
                      : 'bg-red-500/20 text-red-300'
                  }`}
                >
                  {log.new_value ? 'Habilitado' : 'Desabilitado'}
                </span>
                <span className="text-content-muted">
                  por {log.admin_user_name || 'admin removido'} em{' '}
                  {new Date(log.created_at).toLocaleString('pt-BR')}
                </span>
                {log.reason && <span className="text-content-muted italic">— {log.reason}</span>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardFeatureToggleSection;
